import { IChoosenFilterInterface } from 'app/library/all-users/interfaces/choosen-filter.interface';
import { SelectedFilterChipVm, SelectedFilterGroupVm } from 'app/library/all-users/components/selected-filters-chip/selected-filter-chip.vm';
import { BUILTIN_USER_TEXT_FIELDS, BuiltInUserTextFieldKey } from 'app/library/all-users/components/filter/text-filter/built-in-user-text-fields';
import { MaxBrainTag } from 'app/library/tag/models/tag.model';
import { MaxBrainUtils } from 'app/projects/core/src/lib/utils';
import { IAudienceFilterRule, IAudienceFilterRuleValue, IAudienceFilters } from '../interfaces/audience-filters.interface';
import { IAudienceFiltersPayload } from '../interfaces/audience-filters-payload.interface';

export interface IAudienceFilterUnassignedFlags {
    module: boolean;
    experience: boolean;
    team: boolean;
}

const BUILTIN_API_FIELDS: Record<BuiltInUserTextFieldKey, string> = {
    firstName: 'first_name',
    lastName: 'last_name',
    email: 'email',
};

const API_FIELD_TO_BUILTIN: Record<string, BuiltInUserTextFieldKey> = {
    first_name: 'firstName',
    last_name: 'lastName',
    email: 'email',
};

const ENTITY_FIELD_LABELS: Record<string, string> = {
    course: 'MODULE.SINGULAR',
    user_role: 'GENERAL.LABEL.USER_ROLE',
    user_status: 'GENERAL.PROPERTY.STATUS',
    experience: 'ENGAGE.EXPERIENCE.TITLE.SINGULAR',
    team: 'TEAMS.TITLE.SINGULAR',
    team_role: 'USERS.FILTER.TEAM_ROLE.LABEL',
    created_at: 'GENERAL.PROPERTY.CREATED_ON',
    updated_at: 'GENERAL.PROPERTY.LAST_MODIFIED',
};

function normalizeRuleValues(values: IAudienceFilterRule['values']): Array<string | number> {
    if (!values?.length) {
        return [];
    }

    return values.map((entry) => {
        if (typeof entry === 'string' || typeof entry === 'number') {
            return entry;
        }

        return entry.value;
    });
}

function normalizeRuleLabels(values: IAudienceFilterRule['values']): string[] {
    if (!values?.length) {
        return [];
    }

    return values.map((entry) => {
        if (typeof entry === 'string' || typeof entry === 'number') {
            return String(entry);
        }

        return String(entry.label ?? entry.value ?? '');
    });
}

function isKnownEntityField(field: string): boolean {
    return ['course', 'user_role', 'user_status', 'experience', 'team', 'team_role', 'created_at', 'updated_at'].includes(field);
}

export function extractAudienceFilterUnassignedFlags(rules: IAudienceFilterRule[] = []): IAudienceFilterUnassignedFlags {
    return {
        module: rules.some((rule) => rule.field === 'course' && rule.operator === 'not_assigned' && rule.is_valid !== false),
        experience: rules.some((rule) => rule.field === 'experience' && rule.operator === 'not_assigned' && rule.is_valid !== false),
        team: rules.some((rule) => rule.field === 'team' && rule.operator === 'not_assigned' && rule.is_valid !== false),
    };
}

function isAudienceFilterValueInvalid(value: IAudienceFilterRuleValue): boolean {
    if (typeof value === 'object' && value !== null && 'is_valid' in value) {
        return value.is_valid === false;
    }

    return false;
}

function getValidAudienceFilterRuleValues(values: IAudienceFilterRule['values'] = []): IAudienceFilterRule['values'] {
    return values.filter((value) => !isAudienceFilterValueInvalid(value));
}

function isAudienceFilterRuleDisplayable(rule: IAudienceFilterRule): boolean {
    if (rule.operator === 'not_assigned') {
        return rule.is_valid !== false;
    }

    const validValues = getValidAudienceFilterRuleValues(rule.values);

    if (rule.operator === 'between') {
        return validValues.length >= 2 && validValues.length === (rule.values?.length ?? 0);
    }

    return validValues.length > 0;
}

export function getDisplayableAudienceFilterRules(rules: IAudienceFilterRule[] = []): IAudienceFilterRule[] {
    return rules.filter(isAudienceFilterRuleDisplayable).map((rule) => ({
        ...rule,
        values: rule.operator === 'not_assigned' ? rule.values : getValidAudienceFilterRuleValues(rule.values),
    }));
}

export function audienceRulesToChoosenFilters(rules: IAudienceFilterRule[] = []): IChoosenFilterInterface[] {
    const result: IChoosenFilterInterface[] = [];
    const textFilterMap = new Map<string, IChoosenFilterInterface>();

    for (const rule of getDisplayableAudienceFilterRules(rules)) {
        if (rule.operator === 'not_assigned') {
            continue;
        }

        const values = normalizeRuleValues(rule.values);
        const labels = normalizeRuleLabels(rule.values);

        switch (rule.field) {
            case 'course':
                values.forEach((value, index) => {
                    result.push({
                        entity: { id: String(value), type: 'Module' },
                        valueToShow: `Course: ${labels[index] ?? value}`,
                    });
                });
                break;

            case 'user_role':
                values.forEach((value, index) => {
                    result.push({
                        entity: { id: String(value), type: 'UserRole' },
                        valueToShow: `Role: ${labels[index] ?? value}`,
                    });
                });
                break;

            case 'user_status':
                values.forEach((value, index) => {
                    result.push({
                        entity: { id: String(value), type: 'Status' },
                        valueToShow: `Status: ${labels[index] ?? value}`,
                    });
                });
                break;

            case 'experience':
                values.forEach((value, index) => {
                    result.push({
                        entity: { id: String(value), type: 'Experience' },
                        valueToShow: `Experience: ${labels[index] ?? value}`,
                    });
                });
                break;

            case 'team': {
                const excludedTeamIds = normalizeRuleValues(rule.excluded_values || []).map((value) => String(value));

                values.forEach((value, index) => {
                    const teamName = String(labels[index] ?? value);
                    result.push({
                        entity: { id: String(value), type: 'Team', excludedTeamIds: [...excludedTeamIds] },
                        valueToShow: excludedTeamIds.length ? `Team: ${teamName} (partial)` : `Team: ${teamName}`,
                    });
                });
                break;
            }

            case 'team_role':
                values.forEach((value, index) => {
                    result.push({
                        entity: { id: String(value), type: 'TeamRole' },
                        valueToShow: `Team role: ${labels[index] ?? value}`,
                    });
                });
                break;

            case 'created_at':
            case 'updated_at': {
                if (values.length < 2) {
                    break;
                }

                const entityId = rule.field === 'created_at' ? 'CreatedOn' : 'LastModified';
                result.push({
                    entity: { id: entityId, type: entityId as IChoosenFilterInterface['entity']['type'] },
                    attributeName: rule.field,
                    isRange: true,
                    rangeValue1: String(values[0]),
                    rangeValue2: String(values[1]),
                    valueToShow: `${rule.field === 'created_at' ? 'Created on' : 'Last modified'}: ${values[0]} - ${values[1]}`,
                });
                break;
            }

            default:
                if (API_FIELD_TO_BUILTIN[rule.field]) {
                    const builtInKey = API_FIELD_TO_BUILTIN[rule.field];
                    const builtIn = BUILTIN_USER_TEXT_FIELDS[builtInKey];
                    const mode = rule.operator === 'exactly' ? 'eq' : 'like';
                    const existing = textFilterMap.get(builtInKey) ?? {
                        entity: { id: builtIn.id, type: 'UserProfileTextFilter' as const },
                        attributeName: builtIn.attributeName,
                        attributeId: builtIn.id,
                        textFilters: [],
                        valueToShow: `${builtIn.attributeName}: `,
                    };

                    values.forEach((value) => {
                        existing.textFilters!.push({ value: String(value), mode });
                    });
                    existing.valueToShow = `${builtIn.attributeName}: ${existing.textFilters!.map((tf) => tf.value).join(', ')}`;
                    textFilterMap.set(builtInKey, existing);
                    break;
                }

                if (rule.operator === 'between' && values.length >= 2) {
                    result.push({
                        entity: { id: rule.field, type: 'ExtendedAttribute' },
                        attributeName: rule.field,
                        attributeId: rule.field,
                        isRange: true,
                        rangeValue1: values[0],
                        rangeValue2: values[1],
                        valueToShow: `${rule.field}: ${values[0]} - ${values[1]}`,
                    });
                    break;
                }

                if (rule.operator === 'in') {
                    values.forEach((value, index) => {
                        result.push({
                            entity: { id: String(value), type: 'ExtendedAttributeOption' },
                            attributeName: rule.field,
                            attributeId: rule.field,
                            value: String(value),
                            valueToShow: `${rule.field}: ${labels[index] ?? value}`,
                        });
                    });
                    break;
                }

                values.forEach((value) => {
                    result.push({
                        entity: { id: rule.field, type: 'ExtendedAttribute' },
                        attributeName: rule.field,
                        attributeId: rule.field,
                        textFilters: [{ value: String(value), mode: rule.operator === 'exactly' ? 'eq' : 'like' }],
                        valueToShow: `${rule.field}: ${value}`,
                    });
                });
                break;
        }
    }

    return [...result, ...textFilterMap.values()];
}

function toNumericFilterValue(value: string | number): number | string {
    if (typeof value === 'number') {
        return value;
    }

    const trimmed = value.trim();
    if (!trimmed) {
        return value;
    }

    const numeric = Number(trimmed);
    return Number.isInteger(numeric) && String(numeric) === trimmed ? numeric : value;
}

function toNumericFilterValues(values: Array<string | number>): Array<string | number> {
    return values.map((value) => toNumericFilterValue(value));
}

export function choosenFiltersToAudiencePayload(
    filters: IChoosenFilterInterface[],
    unassigned: IAudienceFilterUnassignedFlags
): IAudienceFiltersPayload {
    const rules: IAudienceFiltersPayload['rules'] = [];

    const pushInRule = (field: string, values: Array<string | number>) => {
        if (!values.length) {
            return;
        }

        rules.push({ field, operator: 'in', values: toNumericFilterValues(values) });
    };

    if (unassigned.module) {
        rules.push({ field: 'course', operator: 'not_assigned', values: [] });
    } else {
        pushInRule(
            'course',
            filters.filter((item) => item.entity.type === 'Module').map((item) => item.entity.id)
        );
    }

    if (unassigned.experience) {
        rules.push({ field: 'experience', operator: 'not_assigned', values: [] });
    } else {
        pushInRule(
            'experience',
            filters.filter((item) => item.entity.type === 'Experience').map((item) => item.entity.id)
        );
    }

    if (unassigned.team) {
        rules.push({ field: 'team', operator: 'not_assigned', values: [] });
    } else {
        const teamFilters = filters.filter((item) => item.entity.type === 'Team');
        if (teamFilters.length) {
            const excludedTeamIds = Array.from(new Set(teamFilters.flatMap((item) => item.entity.excludedTeamIds || []).filter(Boolean)));

            rules.push({
                field: 'team',
                operator: 'in',
                values: toNumericFilterValues(teamFilters.map((item) => item.entity.id)),
                ...(excludedTeamIds.length ? { excluded_values: toNumericFilterValues(excludedTeamIds) } : {}),
            });
        }

        pushInRule(
            'team_role',
            filters.filter((item) => item.entity.type === 'TeamRole').map((item) => item.entity.id)
        );
    }

    pushInRule(
        'user_role',
        filters.filter((item) => item.entity.type === 'UserRole').map((item) => item.entity.id)
    );

    pushInRule(
        'user_status',
        filters.filter((item) => item.entity.type === 'Status').map((item) => item.entity.id)
    );

    for (const filter of filters) {
        if (filter.entity.type === 'CreatedOn') {
            rules.push({
                field: 'created_at',
                operator: 'between',
                values: [filter.rangeValue1, filter.rangeValue2],
            });
        }

        if (filter.entity.type === 'LastModified') {
            rules.push({
                field: 'updated_at',
                operator: 'between',
                values: [filter.rangeValue1, filter.rangeValue2],
            });
        }

        if (filter.entity.type === 'UserProfileTextFilter') {
            const builtInKey = filter.entity.id as BuiltInUserTextFieldKey;
            const field = BUILTIN_API_FIELDS[builtInKey] ?? filter.attributeName ?? filter.entity.id;

            for (const textFilter of filter.textFilters ?? []) {
                rules.push({
                    field,
                    operator: textFilter.mode === 'eq' ? 'exactly' : 'contains',
                    values: [textFilter.value],
                });
            }
        }

        if (filter.entity.type === 'ExtendedAttributeOption' && filter.attributeName) {
            const existingRule = rules.find((rule) => rule.field === filter.attributeName && rule.operator === 'in');
            const value = toNumericFilterValue(filter.value ?? filter.entity.id);

            if (existingRule) {
                existingRule.values.push(value);
            } else {
                rules.push({
                    field: filter.attributeName,
                    operator: 'in',
                    values: [value],
                });
            }
        }

        if (filter.entity.type === 'ExtendedAttribute' && filter.attributeName && filter.isRange) {
            rules.push({
                field: filter.attributeName,
                operator: 'between',
                values: [filter.rangeValue1, filter.rangeValue2],
            });
        }

        if (
            filter.entity.type === 'ExtendedAttribute' &&
            filter.attributeName &&
            !filter.isRange &&
            filter.textFilters?.length
        ) {
            for (const textFilter of filter.textFilters) {
                rules.push({
                    field: filter.attributeName,
                    operator: textFilter.mode === 'eq' ? 'exactly' : 'contains',
                    values: [textFilter.value],
                });
            }
        }
    }

    return { rules };
}

export function audienceFiltersResponseToChoosenFilters(filters: IAudienceFilters | null | undefined): IChoosenFilterInterface[] {
    return audienceRulesToChoosenFilters(filters?.rules ?? []);
}

export function getAudienceFilterFieldLabelKey(field: string): string | null {
    if (ENTITY_FIELD_LABELS[field]) {
        return ENTITY_FIELD_LABELS[field];
    }

    if (API_FIELD_TO_BUILTIN[field]) {
        return BUILTIN_USER_TEXT_FIELDS[API_FIELD_TO_BUILTIN[field]].translationKey;
    }

    return null;
}

export function isExtendedAudienceFilterField(field: string): boolean {
    return !isKnownEntityField(field) && !API_FIELD_TO_BUILTIN[field];
}

export function resolveAudienceFilterValueLabel(label: string, translate: (key: string) => string): string {
    if (!label) {
        return '';
    }

    if (label === 'deleted_entity_translation_key') {
        return translate('deleted_entity_translation_key');
    }

    if (label.endsWith('_translation_key') || label.startsWith('extended_attribute_option_')) {
        const translated = translate(label);
        return translated !== label ? translated : label;
    }

    return label;
}

export function resolveAudienceFilterFieldLabel(
    field: string,
    translate: (key: string) => string,
    extendedAttributeLabels: Record<string, string> = {}
): string {
    const labelKey = getAudienceFilterFieldLabelKey(field);

    if (labelKey) {
        return translate(labelKey);
    }

    const extendedAttributeLabel = extendedAttributeLabels[field];
    if (extendedAttributeLabel) {
        return resolveAudienceFilterValueLabel(extendedAttributeLabel, translate);
    }

    if (field.endsWith('_translation_key')) {
        return translate(field);
    }

    return field;
}

export function getAudienceFilterRuleValueId(value: IAudienceFilterRuleValue): string {
    if (typeof value === 'string' || typeof value === 'number') {
        return String(value);
    }

    return String(value.value ?? '');
}

function hasDisplayableRuleLabel(value: IAudienceFilterRuleValue): boolean {
    if (typeof value === 'string' || typeof value === 'number') {
        return false;
    }

    const label = String(value.label ?? '').trim();
    const id = getAudienceFilterRuleValueId(value);

    return !!label && label !== id && !/^experience[_\s-]*\d+$/i.test(label);
}

export interface IAudienceFilterEntityLookups {
    experienceById?: Record<string, string>;
    teamById?: Record<string, string>;
    teamRoleById?: Record<string, string>;
    courseById?: Record<string, string>;
    userRoleById?: Record<string, string>;
    userStatusById?: Record<string, string>;
}

function getEntityLookupForField(field: string, lookups: IAudienceFilterEntityLookups): Record<string, string> | null {
    switch (field) {
        case 'experience':
            return lookups.experienceById ?? null;
        case 'team':
            return lookups.teamById ?? null;
        case 'team_role':
            return lookups.teamRoleById ?? null;
        case 'course':
            return lookups.courseById ?? null;
        case 'user_role':
            return lookups.userRoleById ?? null;
        case 'user_status':
            return lookups.userStatusById ?? null;
        default:
            return null;
    }
}

function enrichAudienceFilterRuleValue(value: IAudienceFilterRuleValue, lookup: Record<string, string>): IAudienceFilterRuleValue {
    const id = getAudienceFilterRuleValueId(value);

    if (!id) {
        return value;
    }

    const resolvedName = lookup[id];

    if (!resolvedName) {
        return value;
    }

    if (hasDisplayableRuleLabel(value)) {
        return value;
    }

    if (typeof value === 'object' && value !== null && 'value' in value) {
        return {
            ...value,
            value: id,
            label: resolvedName,
        };
    }

    return { value: id, label: resolvedName };
}

export function enrichAudienceFilterRulesWithLabels(
    rules: IAudienceFilterRule[] = [],
    lookups: IAudienceFilterEntityLookups = {}
): IAudienceFilterRule[] {
    return rules.map((rule) => {
        const lookup = getEntityLookupForField(rule.field, lookups);

        if (!lookup || rule.operator === 'not_assigned' || !rule.values?.length) {
            return rule;
        }

        return {
            ...rule,
            values: rule.values.map((value) => enrichAudienceFilterRuleValue(value, lookup)),
        };
    });
}

function getAudienceFilterValueDisplayLabel(value: IAudienceFilterRuleValue, translate: (key: string) => string): string {
    if (typeof value === 'string' || typeof value === 'number') {
        return String(value);
    }

    return resolveAudienceFilterValueLabel(String(value.label ?? value.value ?? ''), translate);
}

function capitalizeFilterLabel(label: string): string {
    return MaxBrainUtils.capitalizeFirstCharacter(label?.trim() ?? '');
}

function isAudienceFilterRuleSummarized(rule: IAudienceFilterRule): boolean {
    if (rule.operator === 'not_assigned') {
        return true;
    }

    return (rule.values?.length ?? 0) > 0;
}

export function buildAudienceFilterSummaryGroups(
    rules: IAudienceFilterRule[] = [],
    translate: (key: string) => string,
    extendedAttributeLabels: Record<string, string> = {}
): SelectedFilterGroupVm[] {
    const palette = MaxBrainTag.colorsArray;

    return rules.filter(isAudienceFilterRuleSummarized).map((rule, index) => {
        const categoryLabel = capitalizeFilterLabel(resolveAudienceFilterFieldLabel(rule.field, translate, extendedAttributeLabels));
        const chips: SelectedFilterChipVm[] = [];

        if (rule.operator === 'not_assigned') {
            chips.push({
                chipLabel: capitalizeFilterLabel(translate('GENERAL.LABEL.NOT_ASSIGNED')),
                entityId: rule.field,
                entityType: 'Module',
                isInvalid: rule.is_valid === false,
            });
        } else if (rule.operator === 'between' && rule.values.length >= 2) {
            const valueLabels = rule.values.map((value) => capitalizeFilterLabel(getAudienceFilterValueDisplayLabel(value, translate)));
            const hasInvalidValue = rule.values.some((value) => isAudienceFilterValueInvalid(value));

            chips.push({
                chipLabel: `${valueLabels[0]} - ${valueLabels[1]}`,
                entityId: rule.field,
                entityType: 'CreatedOn',
                isInvalid: rule.is_valid === false || hasInvalidValue,
            });
        } else {
            rule.values.forEach((value, valueIndex) => {
                chips.push({
                    chipLabel: capitalizeFilterLabel(getAudienceFilterValueDisplayLabel(value, translate)),
                    entityId: `${rule.field}-${valueIndex}`,
                    entityType: 'Module',
                    isInvalid: isAudienceFilterValueInvalid(value),
                });
            });
        }

        return {
            groupKey: rule.field,
            categoryLabel,
            color: palette[index % palette.length],
            chips,
            isInvalid: rule.is_valid === false || chips.some((chip) => chip.isInvalid),
        };
    });
}
